// Modules
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import {
  AgreementSelect,
  TeamSelect,
  UserSelect,
  CompanySelect
} from 'components/forms/selects';
import { Form, Subform, SubformHeader } from 'components/forms/form-structure';
import { INVOICE_METHODS, PAYMENT_METHODS, COMPANY_SERVICES } from 'constants';
import { SelectInput, CheckboxGroupInput, CheckboxInput, RegexInput, MoneyInput } from 'components/forms/form-elements';
import BusinessHoursPicker from 'components/forms/inputs/business-hours-picker';
import { convertArrayToOptions } from 'utils/input-utils';
import { handleServerError } from 'utils/form-utils';
import AddressForm from 'components/models/person/address-form';
import Agreement from 'services/data/agreement';
import EmailForm from 'components/models/person/email-form';
import Person from 'services/data/person';
import PhoneNumberForm from 'components/models/person/phone-number-form';
import requiredValidation from 'utils/required-validation';
import * as actions from 'routes/company/store/actions';
import { usePeople } from 'routes/person/store/actions';

const regex = /^[\w\-\s\.\(\)_'\-]+$/;


function getPersonEmailOptions(people) {
  return _(people).map(person => _.map(person.emailAddresses, email => ({
    value: email.id,
    label: `${person.company.nickname || 'No Company'} - ${Person.fullName(person)} - ${email.email}`
  }))).flatten().sortBy('label').value();
}


function getCompanyEmailOptions(company) {
  return _(company.emailAddresses).map(email => ({
    value: email.id,
    label: `${company.nickname} - ${email.email}`
  })).sortBy('label').value();
}


// Component
function CompanyForm({ change, company, currentUser, initialValues, formValues, quickAdd, settings, ...props }) {
  const parentCompanyId = formValues.parentCompany;
  const companyObject = company || { emailAddresses: [] };
  const { data: parentCompany } = actions.useCompany(parentCompanyId, { enabled: !!parentCompanyId }, { load: ['emailAddresses'] });
  const { data: people } = usePeople({}, { load: ['emailAddresses', 'company'] });
  const { hasUserEscalator, billingEmails } = formValues;
  const [showContacts, setShowContacts] = useState(false);

  const emailTypes = useMemo(() => {
    const employees = [];
    const parentEmployees = [];
    const external = [];

    _.forEach(people, person => {
      if (companyObject.id && person.company.id === companyObject.id) return employees.push(person);
      if (parentCompanyId && person.company.id === parentCompanyId) return parentEmployees.push(person);
      return external.push(person);
    });

    return {
      internal: _.concat(
        getCompanyEmailOptions(companyObject),
        getPersonEmailOptions(employees),
        getCompanyEmailOptions(parentCompany || { emailAddresses: [] }),
        getPersonEmailOptions(parentEmployees),
      ),
      external: getPersonEmailOptions(external)
    };
  }, [people, companyObject, parentCompany]);

  const hasExternalBillingEmails = Boolean(_.difference(billingEmails, _.map(emailTypes.internal, 'value')).length);
  const showExternalContacts = hasExternalBillingEmails || showContacts;
  const emails = showExternalContacts ? _.concat(emailTypes.internal, emailTypes.external) : emailTypes.internal;

  const activeServices = _(settings)
    .pickBy((value, key) => _.includes(key, 'CreateCompanyAppUrl'))
    .pickBy(_.identity)
    .keys()
    .map(key => _.replace(key, 'CreateCompanyAppUrl', ''))
    .value();

  const isLead = Person.isLead(currentUser);
  const isClient = companyObject.companyType === 'Client';
  const apps = _.map(convertArrayToOptions(COMPANY_SERVICES), app => {
    const alreadyConnected = _.includes(_.get(initialValues, 'externalAppIds'), app.value);
    const hasURL = _.includes(activeServices, app.value);

    return _.defaults({ disabled: alreadyConnected || !hasURL, label: _.startCase(app.label) }, app);
  });

  if (quickAdd) {
    return (
      <Form onSubmit={props.handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])}>
        <Field component={RegexInput} regex={regex} name="name" label="Name" />
        <PhoneNumberForm />
        <AddressForm change={change} allowBilling />
      </Form>
    );
  }

  return (
    <Form onSubmit={props.handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])}>
      <div className="form-flex-row">
        <Field component={RegexInput} regex={regex} name="name" label="Name" />
        <Field component={RegexInput} regex={regex} name="nickname" label="Nickname" />
      </div>
      {isClient &&
        <div>
          <div className="form-flex-row">
            <Field component={TeamSelect} onChange={() => change('primaryTech', null)} name="team" label="Team" />
            <Field component={UserSelect} team={_.get(formValues, 'team')} name="primaryTech" label="Primary Tech" />
          </div>
          <div className="form-flex-row">
            <Field component={SelectInput} name="paymentMethod" label="Payment Method" options={convertArrayToOptions(PAYMENT_METHODS)} />
            <Field component={SelectInput} name="invoiceMethod" label="Invoice Method" options={convertArrayToOptions(INVOICE_METHODS)} />
          </div>
          <Field component={CheckboxInput} name="purchaseOrderNumberRequired" label="Require Purchase Order Number on Invoice" />
          <Field component={CheckboxInput} name="hideBillDetails" label="Hide time report details on invoices by default" />
          <Field component={AgreementSelect} name="defaultAgreement" label="Default Agreement" company={companyObject.id} searchParams={Agreement.activeQuery} />
          <Field component={CompanySelect} name="parentCompany" label="Parent Company" />
          <Field component={CheckboxInput} name="hasUserEscalator" label="This client has a user elevator on their ongoing agreement" />
          {hasUserEscalator && <Field component={MoneyInput} name="userEscalatorAmount" label="User elevator amount" />}
          <Field component={CheckboxInput} name="disableAutofill" label={<span>Disable autofill of company for triage items<br />(to prevent tickets in the wrong company)</span>} />
        </div>
      }
      {isLead && isClient &&
        <Subform>
          <SubformHeader>Connected Apps</SubformHeader>
          <Field component={CheckboxGroupInput} name="externalAppIds" options={apps} />
        </Subform>
      }
      {isClient &&
        <Subform className="billing-email-select-subform">
          <SubformHeader>Billing Emails</SubformHeader>
          <div className="checkbox-wrapper form-element">
            <label className="no-select">
              <input type="checkbox" className="form-control" disabled={hasExternalBillingEmails} checked={showExternalContacts} onChange={event => setShowContacts(event.target.checked)} />
              <span>Show all contacts</span>
            </label>
          </div>
          {!emails.length && <p>No available emails for billing, please go add a person or company email.</p>}
          <Field elementClasses="billing-email-select" component={CheckboxGroupInput} searchEnabled options={emails} name="billingEmails" />
          {hasExternalBillingEmails &&
            <div className="alert alert-danger m-0 w-100 mw-100">
              You've chosen to send billing emails to a contact that is not a part of this company or its parent company
              are you sure you want to do that?
            </div>
          }
        </Subform>
      }
      <EmailForm />
      <PhoneNumberForm />
      <AddressForm change={change} allowBilling />
      {isClient &&
        <Subform className="business-hours-select-subform">
          <SubformHeader>Business Hours</SubformHeader>
          <p>
            After hours notifications will not send a critical alert until 2 hours before the business opens.
            <b>To indicate a business is open 24 hours check the box and leave the hours blank.</b>
          </p>
          <Field name="sundayHours" component={BusinessHoursPicker} label="Sunday" />
          <Field name="mondayHours" component={BusinessHoursPicker} label="Monday" />
          <Field name="tuesdayHours" component={BusinessHoursPicker} label="Tuesday" />
          <Field name="wednesdayHours" component={BusinessHoursPicker} label="Wednesday" />
          <Field name="thursdayHours" component={BusinessHoursPicker} label="Thursday" />
          <Field name="fridayHours" component={BusinessHoursPicker} label="Friday" />
          <Field name="saturdayHours" component={BusinessHoursPicker} label="Saturday" />
        </Subform>
      }
    </Form>
  );
}

CompanyForm.propTypes = {
  change: PropTypes.func,
  company: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  formValues: PropTypes.object,
  currentUser: PropTypes.object,
  quickAdd: PropTypes.bool,
  employees: PropTypes.array,
  settings: PropTypes.object
};

const ConnectedForm = connect((state, ownProps) => ({
  formValues: formValueSelector(ownProps.form)(state, 'team', 'primaryTech', 'hasUserEscalator', 'parentCompany', 'billingEmails'),
  currentUser: state.auth.currentUser,
  settings: state.pageData.settings
}))(CompanyForm);

export default reduxForm({
  form: 'company-form',
  validate: (values, props) => {
    const errors = {};
    const invoiceMethod = values.invoiceMethod;
    const isClient = _.get(props.company, 'companyType') === 'Client';
    const required = requiredValidation(['name'], values);

    if (isClient) _.assign(required, requiredValidation(['nickname', 'invoiceMethod', 'paymentMethod', 'team', 'primaryTech'], values));

    errors.emailAddresses = _.map(values.emailAddresses, email => requiredValidation(['email', 'type'], email));
    errors.phoneNumbers = _.map(values.phoneNumbers, number => requiredValidation(['number', 'type'], number));
    errors.addresses = _.map(values.addresses, address => requiredValidation(['address', 'city', 'state', 'zip', 'type'], address));

    if (invoiceMethod === 'Mail' && !_.some(values.addresses, { billing: true })) errors.addresses._error = 'A billing address is required for mailed invoices!';
    if (invoiceMethod === 'Email' && !values.billingEmails.length) errors.billingEmails = 'At least 1 billing email is required for emailed invoices!';

    if (!errors.name && !regex.test(values.name)) errors.name = 'Invalid (punctation or symbols)';
    if (!errors.nickname && !regex.test(values.nickname)) errors.nickname = 'Invalid (punctation or symbols)';

    if (values.sundayHours && values.sundayHours.start && values.sundayHours.end) {
      const hours = moment(values.sundayHours.end, 'HH:mm').diff(moment(values.sundayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.sundayHours = 'Open must be before close';
    }

    if (values.mondayHours && values.mondayHours.start && values.mondayHours.end) {
      const hours = moment(values.mondayHours.end, 'HH:mm').diff(moment(values.mondayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.mondayHours = 'Open must be before close';
    }

    if (values.tuesdayHours && values.tuesdayHours.start && values.tuesdayHours.end) {
      const hours = moment(values.tuesdayHours.end, 'HH:mm').diff(moment(values.tuesdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.tuesdayHours = 'Open must be before close';
    }

    if (values.wednesdayHours && values.wednesdayHours.start && values.wednesdayHours.end) {
      const hours = moment(values.wednesdayHours.end, 'HH:mm').diff(moment(values.wednesdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.wednesdayHours = 'Open must be before close';
    }

    if (values.thursdayHours && values.thursdayHours.start && values.thursdayHours.end) {
      const hours = moment(values.thursdayHours.end, 'HH:mm').diff(moment(values.thursdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.thursdayHours = 'Open must be before close';
    }

    if (values.fridayHours && values.fridayHours.start && values.fridayHours.end) {
      const hours = moment(values.fridayHours.end, 'HH:mm').diff(moment(values.fridayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.fridayHours = 'Open must be before close';
    }

    if (values.saturdayHours && values.saturdayHours.start && values.saturdayHours.end) {
      const hours = moment(values.saturdayHours.end, 'HH:mm').diff(moment(values.saturdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) errors.saturdayHours = 'Open must be before close';
    }

    const allErrors = _.assign(errors, required);

    return allErrors;
  },
  warn: (values) => {
    const warnings = {};

    if (values.mondayHours && !values.mondayHours.open) warnings.mondayHours = 'Are you sure this business is closed on Mondays?';
    if (values.tuesdayHours && !values.tuesdayHours.open) warnings.tuesdayHours = 'Are you sure this business is closed on Tuesdays?';
    if (values.wednesdayHours && !values.wednesdayHours.open) warnings.wednesdayHours = 'Are you sure this business is closed on Wednesdays?';
    if (values.thursdayHours && !values.thursdayHours.open) warnings.thursdayHours = 'Are you sure this business is closed on Thursdays?';
    if (values.fridayHours && !values.fridayHours.open) warnings.fridayHours = 'Are you sure this business is closed on Fridays?';

    if (values.sundayHours && values.sundayHours.start && values.sundayHours.end) {
      const hours = moment(values.sundayHours.end, 'HH:mm').diff(moment(values.sundayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.sundayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.sundayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.mondayHours && values.mondayHours.start && values.mondayHours.end) {
      const hours = moment(values.mondayHours.end, 'HH:mm').diff(moment(values.mondayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.mondayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.mondayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.tuesdayHours && values.tuesdayHours.start && values.tuesdayHours.end) {
      const hours = moment(values.tuesdayHours.end, 'HH:mm').diff(moment(values.tuesdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.tuesdayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.tuesdayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.wednesdayHours && values.wednesdayHours.start && values.wednesdayHours.end) {
      const hours = moment(values.wednesdayHours.end, 'HH:mm').diff(moment(values.wednesdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.wednesdayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.wednesdayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.thursdayHours && values.thursdayHours.start && values.thursdayHours.end) {
      const hours = moment(values.thursdayHours.end, 'HH:mm').diff(moment(values.thursdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.thursdayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.thursdayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.fridayHours && values.fridayHours.start && values.fridayHours.end) {
      const hours = moment(values.fridayHours.end, 'HH:mm').diff(moment(values.fridayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.fridayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.fridayHours = 'This is less than 8 hours, are you sure?';
    }

    if (values.saturdayHours && values.saturdayHours.start && values.saturdayHours.end) {
      const hours = moment(values.saturdayHours.end, 'HH:mm').diff(moment(values.saturdayHours.start, 'HH:mm'), 'hours');
      if (hours < 0) warnings.saturdayHours = 'Open is after close, did you swap open and close?';
      if (hours > 0 && hours < 8) warnings.saturdayHours = 'This is less than 8 hours, are you sure?';
    }

    return warnings;
  },
  onSubmitFail: handleServerError
})(ConnectedForm);



// WEBPACK FOOTER //
// ./app/components/models/company/company-form.js